<!--
  Booking — lista unificata. Le card in alto sono ANCHE il selettore di stato,
  i dati arrivano per finestre temporali e i filtri li applica il database.

  NOTA SULLE RIGHE — un booking contiene più TRATTE (andata/ritorno) nelle
  collezioni pickup_bookings / escort_bookings. A video ogni tratta è una riga
  a sé: per questo il piede distingue "booking" da "tratte".

  ⚠️ LA CARD "RICHIESTE" È UN MONDO A PARTE — le richieste dell'app cliente
  (booking_request) NON sono prenotazioni: stanno in un'altra tabella e
  diventano booking solo dopo la conversione. Non hanno tratte né driver, e
  nelle altre card non compaiono. Per questo il segmento cambia sorgente
  (/booking-request invece di /booking + /journey) e due colonne.

  JOLLY SUI BOOKING — vive su `planner`, non sulla gamba, e si legge con i
  join `*_bookings.planners` + `.planners.jolly`. ⚠️ `planner.jolly` NON è il
  jolly: è il driver assegnato a quel planner; il ruolo lo dice `planner.type`.
-->
<template>
  <div class="bkl">
    <!-- intestazione -->
    <div class="bkl__head">
      <div>
        <h1 class="bkl__title">
          {{ currentProvince ? `Booking · ${currentProvince.name}` : 'Booking' }}
        </h1>
        <p class="bkl__sub">
          <template v-if="isRequests">
            Richieste arrivate dall'app cliente: <strong>non sono ancora prenotazioni</strong> —
            vanno valutate e poi convertite in booking.
          </template>
          <template v-else>
            Tutte le prenotazioni in ordine cronologico — pickup, transfer e notturno.
          </template>
        </p>
      </div>
      <div class="bkl__headActions">
        <el-input
          v-model="search"
          class="bkl__search"
          placeholder="Cerca per nome o cognome del cliente…"
          clearable
          @input="onSearchInput"
        />
        <router-link
          to="/estimate"
          class="routerLink"
        >
          <el-button>Calcola preventivo</el-button>
        </router-link>
        <!-- Gli Spostamenti Interni si creano da qui, e questo elenco mostra già
             le loro tratte, una riga per corsa.

             I due colori NON sono decorativi: sono gli stessi che ServiceChip
             usa nelle righe sotto — viola per lo spostamento interno, giallo
             per il pickup. Il pulsante ha così il colore di ciò che crea. -->
        <router-link
          to="/journey/new"
          class="routerLink"
        >
          <el-button class="bkl__cta bkl__cta--fleet">
            Nuovo percorso
          </el-button>
        </router-link>
        <router-link
          to="/booking/new"
          class="routerLink"
        >
          <el-button class="bkl__cta bkl__cta--booking">
            Nuova prenotazione
          </el-button>
        </router-link>
      </div>
    </div>

    <!-- card di riepilogo = selettore di stato.
         Prima riga: quello che aspetta una nostra mossa (richieste da
         valutare, tratte da assegnare, corse passate mai chiuse).
         Seconda riga: il ciclo normale di una prenotazione. -->
    <div class="bkl__kpis bkl__kpis--todo">
      <button
        v-for="k in kpisTodo"
        :key="k.key"
        type="button"
        class="bkl__kpi"
        :class="{ 'is-active': segment === k.key, 'is-attn': k.attn, 'is-req': k.key === 'richieste' }"
        @click="setSegment(k.key)"
      >
        <span class="bkl__kpiNum">{{ k.value === null ? '—' : k.value }}</span>
        <span class="bkl__kpiLbl">{{ k.label }}</span>
        <span
          v-if="k.hint"
          class="bkl__kpiHint"
        >{{ k.hint }}</span>
      </button>
    </div>

    <div class="bkl__kpis bkl__kpis--ciclo">
      <button
        v-for="k in kpisCiclo"
        :key="k.key"
        type="button"
        class="bkl__kpi"
        :class="{ 'is-active': segment === k.key, 'is-attn': k.attn }"
        @click="setSegment(k.key)"
      >
        <span class="bkl__kpiNum">{{ k.value === null ? '—' : k.value }}</span>
        <span class="bkl__kpiLbl">{{ k.label }}</span>
        <span
          v-if="k.hint"
          class="bkl__kpiHint"
        >{{ k.hint }}</span>
      </button>
    </div>

    <div class="bkl__card">
      <!-- filtri -->
      <div class="bkl__toolbar">
        <!-- Solo sulle richieste: la coda di lavoro sono le "da valutare",
             ma gli altri stati devono restare consultabili. -->
        <div
          v-if="isRequests"
          class="bkl__field"
        >
          <label>Stato richiesta</label>
          <el-select
            v-model="requestStatus"
            placeholder="Tutte"
            clearable
            style="width: 180px"
            @change="page = 1"
          >
            <el-option
              label="Da valutare"
              value="pending"
            />
            <el-option
              label="Contro-proposte"
              value="counter_proposed"
            />
            <el-option
              label="Approvate"
              value="approved"
            />
            <el-option
              label="Rifiutate"
              value="rejected"
            />
          </el-select>
        </div>
        <div class="bkl__field">
          <label>Servizio</label>
          <el-select
            v-model="serviceFilter"
            placeholder="Tutti"
            clearable
            style="width: 190px"
          >
            <el-option
              label="Pickup & Delivery"
              value="pickup"
            />
            <el-option
              label="Transfer"
              value="escort"
            />
            <el-option
              label="Notturno"
              value="restaurant"
            />
            <!-- gli spostamenti interni non nascono mai da una richiesta -->
            <el-option
              v-if="!isRequests"
              label="Spostamento interno"
              value="journey"
            />
          </el-select>
        </div>
        <!-- La provincia sta nell'indirizzo (?provincia=), come dal menu:
             cambiarla qui aggiorna anche la voce evidenziata a sinistra. -->
        <div class="bkl__field">
          <label>Provincia</label>
          <el-select
            :model-value="provinceId"
            placeholder="Tutte"
            clearable
            filterable
            style="width: 180px"
            @update:model-value="setProvince"
          >
            <el-option
              v-for="p in provinces"
              :key="p.id"
              :label="p.name"
              :value="p.id"
            />
          </el-select>
        </div>
        <div class="bkl__field">
          <label>Partner</label>
          <el-select
            v-model="partnerId"
            placeholder="Tutti"
            clearable
            filterable
            style="width: 210px"
            @change="reload"
          >
            <el-option
              v-for="p in partners"
              :key="p.id"
              :label="p.company_name"
              :value="p.id"
            />
          </el-select>
        </div>
        <div class="bkl__spacer" />
        <el-button
          text
          @click="resetFilters"
        >
          Azzera filtri
        </el-button>
      </div>

      <!-- Gli spostamenti interni escono dalla lista mentre si cerca: senza
           dirlo sembrerebbero spariti. -->
      <div
        v-if="search.trim() && !isRequests"
        class="bkl__note"
      >
        La ricerca è per nome o cognome del cliente: gli <b>spostamenti interni</b> non ne
        hanno uno e restano esclusi finché il campo è compilato.
      </div>

      <!-- tabella -->
      <el-table
        v-loading="loading"
        :data="visibleRows"
        style="width: 100%"
        :empty-text="emptyText"
        row-key="_rowKey"
        :row-class-name="rowClass"
        @row-click="openDetail"
      >
        <el-table-column
          label="Orario & servizio"
          min-width="190"
        >
          <template #default="scope">
            <div class="bkl__when">
              <div class="bkl__times">
                <span class="bkl__tl">Part</span>
                <span class="bkl__tt">{{ hhmm(scope.row.start) }}</span>
              </div>
              <div class="bkl__times bkl__times--arr">
                <span class="bkl__tl">{{ scope.row.endLabel || 'Arr' }}</span>
                <span class="bkl__tt">{{ hhmm(scope.row.end) }}</span>
              </div>
              <div class="bkl__chips">
                <span class="bkl__day">{{ dayLabel(scope.row.date) }}</span>
                <ServiceChip
                  :service="scope.row.service"
                  :label="scope.row.notturno && scope.row.service?.startsWith('escort') ? 'Transfer · Notturno' : ''"
                />
                <!-- marchio esplicito: una richiesta non è una prenotazione -->
                <span
                  v-if="scope.row.kind === 'request'"
                  class="bkl__req"
                >Richiesta</span>
                <span
                  v-if="scope.row.direction"
                  class="bkl__leg"
                >{{ scope.row.direction }}</span>
                <!-- Spostamento interno: singolo (driver e jolly) o a catena. -->
                <span
                  v-if="scope.row.tipo"
                  class="bkl__tipo"
                >{{ scope.row.tipo }}</span>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Percorso"
          min-width="260"
        >
          <template #default="scope">
            <div class="bkl__route">
              <div class="bkl__pt">
                <span class="bkl__pin bkl__pin--from" />
                <span class="bkl__addr">{{ scope.row.from || '—' }}</span>
              </div>
              <div class="bkl__pt">
                <span class="bkl__pin bkl__pin--to" />
                <span class="bkl__addr">{{ scope.row.to || '—' }}</span>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="N° & cliente"
          min-width="170"
        >
          <template #default="scope">
            <template v-if="scope.row.kind === 'journey'">
              <div class="bkl__cust">
                {{ scope.row.veicolo || '—' }}
              </div>
              <div
                v-if="scope.row.targa || scope.row.prova"
                class="bkl__meta bkl__plate"
              >
                <span v-if="scope.row.targa">{{ scope.row.targa }}</span>
                <!-- Veicolo non immatricolato: si sposta con la targa prova. -->
                <span
                  v-if="scope.row.prova"
                  class="bkl__prova"
                  title="Veicolo non immatricolato: si sposta con la targa prova"
                >Targa prova</span>
              </div>
            </template>
            <template v-else>
              <div class="bkl__cust">
                {{ scope.row.customer }}
              </div>
              <div class="bkl__meta">
                {{ scope.row.phone }}
              </div>
              <div class="bkl__id">
                #{{ scope.row.shortId }}
              </div>
            </template>
          </template>
        </el-table-column>

        <el-table-column
          label="Partner"
          min-width="150"
        >
          <template #default="scope">
            <!-- Sulle richieste il partner può ancora mancare (notturno con
                 partenza libera, transfer): va assegnato prima di convertire. -->
            <span
              v-if="scope.row.kind === 'request' && !scope.row.partnerId"
              class="bkl__unassigned"
            >Partner da assegnare</span>
            <div v-else>
              {{ partnerOf(scope.row) }}
            </div>
            <div class="bkl__meta">
              {{ scope.row.headquarter }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          label="Stato"
          width="150"
        >
          <template #default="scope">
            <StatusPill
              :kind="scope.row.stateKind"
              :label="scope.row.stateLabel"
            />
            <div
              v-if="scope.row.bookingId"
              class="bkl__meta"
            >
              già convertita
            </div>
          </template>
        </el-table-column>

        <!-- Le richieste non hanno tratte: niente driver da assegnare finché
             non diventano un booking. Al suo posto serve sapere da quanto
             sono ferme in coda. -->
        <el-table-column
          v-if="isRequests"
          label="Ricevuta"
          min-width="150"
        >
          <template #default="scope">
            <div>{{ dayLabel(scope.row.submittedAt) }} · {{ hhmm(scope.row.submittedAt) }}</div>
            <div
              class="bkl__meta"
              :class="{ 'bkl__meta--stale': scope.row.waitingDays >= 2 }"
            >
              {{ waitLabel(scope.row) }}
            </div>
          </template>
        </el-table-column>

        <el-table-column
          v-if="!isRequests"
          label="Driver / Jolly"
          min-width="170"
        >
          <template #default="scope">
            <div
              v-if="scope.row.driver"
              class="bkl__driver"
            >
              <span class="bkl__avatar">{{ ini(scope.row.driver) }}</span>
              {{ scope.row.driver }}
            </div>
            <span
              v-else
              class="bkl__unassigned"
            >Driver non assegnato</span>

            <!-- Il jolly è disponibile solo sugli spostamenti interni: sui
                 booking vive su `planner`, che /booking non espone. -->
            <div
              v-if="scope.row.jolly"
              class="bkl__driver bkl__driver--jolly"
            >
              <span class="bkl__avatar bkl__avatar--j">{{ ini(scope.row.jolly) }}</span>
              {{ scope.row.jolly }}
              <span class="bkl__role">jolly</span>
            </div>
            <span
              v-else-if="scope.row.needsJolly"
              class="bkl__unassigned"
            >Jolly non assegnato</span>
          </template>
        </el-table-column>

        <el-table-column
          width="120"
          align="right"
          label="Azioni"
          fixed="right"
        >
          <template #default="scope">
            <el-button
              size="small"
              type="primary"
              @click.stop="openDetail(scope.row)"
            >
              Dettagli
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- Conclusi: si scorre lo storico un mese per volta, così
           prenotazioni e spostamenti restano uniti e in ordine. -->
      <div
        v-if="segment === 'passati'"
        class="bkl__storico"
      >
        <span>Stai vedendo {{ storicoLabel }}</span>
        <el-button
          size="small"
          :loading="caricandoMese"
          @click="caricaMesePrecedente"
        >
          Carica {{ mesePrecedenteLabel }}
        </el-button>
      </div>

      <!-- piede -->
      <div class="bkl__foot">
        <span class="bkl__footInfo">
          {{ total }} {{ isRequests ? 'richieste' : 'tratte' }}
          <template v-if="pageCount > 1">· pagina {{ page }} di {{ pageCount }}</template>
          <span
            v-if="capped && !isRequests"
            class="bkl__capped"
          >· superato il limite di {{ windowCap }} prenotazioni per finestra: restringi il periodo o filtra per partner</span>
        </span>
        <el-pagination
          background
          layout="prev, pager, next"
          :page-size="pageSize"
          :total="total"
          :current-page="page"
          @current-change="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import areasAPI from '../../services/areasAPI.js';
import bookingAPI, { segmentWindow, WINDOW_CAP } from '../../services/bookingAPI.js';
import bookingRequestAPI, { requestState } from '../../services/bookingRequestAPI.js';
import journeyAPI from '../../services/journeyAPI.js';
import partnerAPI from '../../services/partnerAPI.js';
import StatusPill from '../../components/ui/StatusPill.vue';
import ServiceChip from '../../components/ui/ServiceChip.vue';

const PAGE_SIZE = 25;

// Nomi dei mesi per lo storico dei «Conclusi» (stessi della contabilità).
const MONTHS = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

// Stato del booking -> pill. Gli stati intermedi della macchina a stati del
// driver (startAPickup, photoRPickup, ...) sono decine: qualunque cosa non sia
// iniziale/finale significa "in corso".
function stateOf(state) {
  if (state === 'end') return { kind: 'muted', label: 'Concluso' };
  if (state === 'canceled') return { kind: 'danger', label: 'Annullato' };
  if (state === 'before') return { kind: 'attn', label: 'Non ancora confermata' };
  if (state === 'accepted') return { kind: 'ok', label: 'Accettato' };
  return { kind: 'active', label: 'In corso' };
}

const initials = (name) =>
  (name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

const place = (p) =>
  p ? [p.address, p.street_number, p.city].filter(Boolean).join(' ').trim() : '';

// Le richieste tengono data e ora in due colonne separate ('2026-09-01',
// '10:30'), e l'ora può mancare: in quel caso non si finge un orario.
const joinDateTime = (date, time) =>
  date && time ? new Date(`${date}T${String(time).slice(0, 5)}:00`) : null;

const DAY_MS = 24 * 60 * 60 * 1000;

export default {
  name: 'BookingPage',
  components: { StatusPill, ServiceChip },

  setup() {
    const router = useRouter();
    const route = useRoute();

    const segment = ref('assegnare');
    const rows = ref([]);
    const loading = ref(false);
    const page = ref(1);
    const capped = ref(false);
    const search = ref('');
    const partnerId = ref('');
    const serviceFilter = ref('');
    const partners = ref([]);
    // Provincia scelta dal menu Booking (?provincia=). L'indirizzo è
    // la fonte: menu, filtro e link condiviso dicono la stessa cosa.
    const provinces = ref([]);
    const provinceId = computed(() => String(route.query.provincia || ''));
    const currentProvince = computed(
      () => provinces.value.find((p) => p.id === provinceId.value) || null,
    );
    // sotto-filtro della sola card "Richieste": la coda di lavoro è `pending`
    const requestStatus = ref('pending');

    // CONCLUSI: un mese per volta, dal più recente. `mesiCaricati` tiene i mesi
    // già in lista, così "Carica il mese precedente" li accoda senza perdere
    // l'ordine e senza spezzare l'unione fra prenotazioni e spostamenti.
    const meseCorrente = () => {
      const oggi = new Date();
      return { year: oggi.getFullYear(), month: oggi.getMonth() + 1 };
    };
    const mesiCaricati = ref([meseCorrente()]);
    const caricandoMese = ref(false);
    const meseLabel = (m) => `${MONTHS[m.month - 1]} ${m.year}`;
    const mesePrima = (m) => (m.month === 1
      ? { year: m.year - 1, month: 12 }
      : { year: m.year, month: m.month - 1 });
    const storicoLabel = computed(() => {
      const primo = mesiCaricati.value[mesiCaricati.value.length - 1];
      const ultimo = mesiCaricati.value[0];
      return mesiCaricati.value.length === 1
        ? meseLabel(ultimo)
        : `da ${meseLabel(primo)} a ${meseLabel(ultimo)}`;
    });
    const mesePrecedenteLabel = computed(() =>
      meseLabel(mesePrima(mesiCaricati.value[mesiCaricati.value.length - 1])));

    const counts = ref({
      richieste: null,
      assegnare: null, conferma: null, attivi: null, futuri: null, passati: null,
      dachiudere: null,
    });

    // Totali di TUTTO lo storico, chiesti al server (una riga, si legge il
    // conteggio): le card sopra contano le tratte della finestra mostrata,
    // questi contano prenotazioni e spostamenti in tutto. `null` = non
    // disponibile, e allora non si scrive un numero inventato.
    const totConcluse = ref(null);
    const totConclusi = ref(null);
    const conclusiHint = computed(() => {
      const b = totConcluse.value;
      const j = totConclusi.value;
      if (b === null && j === null) return '';
      const parti = [
        `${b === null ? '—' : b} ${b === 1 ? 'prenotazione' : 'prenotazioni'}`,
        `${j === null ? '—' : j} ${j === 1 ? 'spostamento' : 'spostamenti'}`,
      ];
      if (b !== null && j !== null) parti.push(`${b + j} in tutto`);
      return parti.join(' · ');
    });

    const isRequests = computed(() => segment.value === 'richieste');

    // Prima riga: quello che aspetta una nostra mossa.
    const kpisTodo = computed(() => [
      { key: 'richieste', label: 'Richieste tramite app', value: counts.value.richieste, attn: true },
      { key: 'assegnare', label: 'Da assegnare', value: counts.value.assegnare, attn: true },
      { key: 'dachiudere', label: 'Da chiudere', value: counts.value.dachiudere, attn: true },
    ]);

    // Seconda riga: il ciclo normale di una prenotazione.
    const kpisCiclo = computed(() => [
      { key: 'conferma', label: 'In conferma', value: counts.value.conferma },
      { key: 'attivi', label: 'Attivi', value: counts.value.attivi },
      { key: 'futuri', label: 'Futuri', value: counts.value.futuri },
      { key: 'passati', label: 'Conclusi', value: counts.value.passati, hint: conclusiHint.value },
    ]);

    // Il server manda solo partnerId: il nome sta nell'elenco già caricato
    // per il filtro.
    const partnerMap = computed(() =>
      Object.fromEntries(partners.value.map((p) => [p.id, p.company_name])),
    );
    const partnerOf = (row) =>
      row.partner || partnerMap.value[row.partnerId] || '—';

    const driverName = (d) =>
      d ? [d.name, d.surname].filter(Boolean).join(' ') : '';

    // ---- da booking annidati a righe piatte (una per tratta) -------------
    // Pickup: la tratta ha UN solo punto (casa cliente); l'altro capo è la
    // sede del partner, e la direzione decide quale dei due è la partenza.
    // Escort/Transfer: entrambi i punti stanno sulla tratta.
    //
    // ORARI:
    //   andata  -> partenza = date_customer, arrivo = date_partner
    //   ritorno -> l'opposto: partenza = date_partner, arrivo = date_customer
    //   Vale anche per il transfer: andata casa → sede, ritorno sede → casa.
    // Jolly di una gamba, dai suoi planner.
    //
    // ⚠️ `planner.jolly` NON è il jolly: è il DRIVER ASSEGNATO a quel planner.
    // Il ruolo lo dice `planner.type`. Quindi il jolly della corsa è il driver
    // del planner di tipo 'jolly', e l'esistenza stessa di quel planner dice
    // che la corsa richiede due operatori.
    const jollyOf = (leg) => {
      const pl = (leg.planners || []).find((p) => p?.type === 'jolly');
      return {
        needsJolly: !!pl,
        jolly: driverName(pl?.jolly),
      };
    };

    const toRows = (booking) => {
      const out = [];
      const st = stateOf(booking.state);
      const base = {
        id: booking.id,
        shortId: (booking.id || '').slice(0, 8),
        customer: [booking.customer?.name, booking.customer?.surname]
          .filter(Boolean).join(' ') || '—',
        phone: booking.customer?.telephone_number || '',
        partner: booking.headquarter?.partner?.company_name || '',
        headquarter: booking.headquarter?.name || booking.headquarter?.city || '',
        date: booking.data,
        stateKind: st.kind,
        stateLabel: st.label,
        kind: 'booking',
      };
      const hqPlace = place(booking.headquarter) || booking.headquarter?.name || 'Sede partner';
      // A UI il servizio "Notturno" è il type_booking 'restaurant', che segue
      // il PARTNER: anche il Transfer di un partner notturno ce l'ha. Il
      // servizio lo dice `type_prenotazione`: un Transfer resta un Transfer
      // (con la nota «Notturno»), e il filtro Transfer lo trova.
      const notturno = booking.type_booking === 'restaurant';
      const transfer = String(booking.type_prenotazione || '').startsWith('escort');
      const serviceOf = () => (transfer
        ? booking.type_prenotazione
        : notturno ? 'restaurant' : booking.type_prenotazione || 'pickup');

      // Partenza e arrivo di una tratta. La convenzione è
      // partenza = `date_customer`, arrivo = `date_partner` (invertite sul
      // ritorno).
      //
      // Sulle tratte ESCORT `date_customer` può essere NULL: quando una delle
      // due manca si mostra quella che c'è al posto della partenza.
      const legTimes = (leg, goes) => {
        const a = goes ? leg.date_customer : leg.date_partner;
        const b = goes ? leg.date_partner : leg.date_customer;
        if (a && b) return { start: a, end: b };
        return { start: a || b || null, end: null };
      };

      (booking.pickup_bookings || []).forEach((leg, i) => {
        const home = place(leg.point);
        const goes = leg.type_booking !== 'return';
        out.push({
          ...base,
          ...legTimes(leg, goes),
          _rowKey: `${booking.id}-p${i}`,
          service: serviceOf(),
          notturno,
          direction: goes ? '↗ Andata' : '↘ Ritorno',
          from: goes ? home : hqPlace,
          to: goes ? hqPlace : home,
          driver: driverName(leg.driver_pickup),
          ...jollyOf(leg),
        });
      });

      (booking.escort_bookings || []).forEach((leg, i) => {
        const goes = leg.type_booking !== 'return';
        out.push({
          ...base,
          ...legTimes(leg, goes),
          _rowKey: `${booking.id}-e${i}`,
          service: transfer ? booking.type_prenotazione : 'escort',
          notturno,
          direction: goes ? '↗ Andata' : '↘ Ritorno',
          from: place(leg.point_origin_escort),
          to: place(leg.point_destination_escort),
          driver: driverName(leg.driver_escort),
          ...jollyOf(leg),
        });
      });

      // Booking senza tratte: non deve sparire dalla lista.
      if (!out.length) {
        out.push({
          ...base,
          _rowKey: booking.id,
          service: serviceOf(),
          notturno,
          direction: '',
          from: hqPlace,
          to: '',
          start: booking.data,
          end: null,
          driver: '',
          jolly: '',
        });
      }
      return out;
    };

    // ---- spostamenti interni (journey): una riga per tratta --------------
    // Vivono in un'altra tabella ma nella board stanno insieme agli altri.
    // Qui il jolly è disponibile (routes.jolly_driver), a differenza dei
    // booking dove vive su `planner` e /booking non lo espone.
    // ORARI: partenza = dateStart, arrivo = dateEnd.
    const journeyToRows = (journey) =>
      (journey.routes || [])
        .filter((r) => r.status !== 'ELIMINATA')
        .map((r, i) => ({
          id: journey.id,
          _rowKey: `${journey.id}-r${i}`,
          kind: 'journey',
          service: 'journey',
          direction: '',
          // Niente cliente né numero: lo spostamento interno lo dice già il
          // servizio. Nella colonna va l'auto che si sposta.
          shortId: '',
          customer: '',
          veicolo: [r.marca, r.modello].filter(Boolean).join(' '),
          targa: r.targa || '',
          tipo: r.two_operators === false ? 'A catena' : 'Singolo',
          prova: r.immatricolato === false,
          phone: '',
          partner: journey.partner?.company_name || '',
          headquarter: '',
          date: journey.date,
          start: r.dateStart,
          end: r.dateEnd,
          from: place(r.startPoint),
          to: place(r.endPoint),
          driver: driverName(r.driver),
          jolly: driverName(r.jolly_driver),
          // Una tratta a 2 operatori è "scoperta" anche se ha solo il driver,
          // salvo che il driver rientri con i mezzi pubblici (deciso da Simba).
          needsJolly: r.two_operators === true && !r.rientro_altrove,
          // Conclusa: dal gestionale, o dal driver che ha consegnato l'auto.
          conclusa: r.status === 'CONCLUSA' || r.execution_status === 'CONSEGNATO',
          ...journeyState(r.status, r.execution_status),
        }));

    // L'avanzamento dall'app del driver si vede al posto della pianificazione
    // finché la corsa non è conclusa.
    const AVANZAMENTO = {
      IN_PARTENZA: 'Driver in partenza',
      IN_VIAGGIO: 'Driver in viaggio',
      ARRIVATO: 'Driver arrivato',
      CONSEGNATO: 'Veicolo consegnato',
    };
    const journeyState = (status, execution) => {
      if (status !== 'CONCLUSA' && AVANZAMENTO[execution]) {
        return {
          stateKind: execution === 'CONSEGNATO' ? 'muted' : 'active',
          stateLabel: AVANZAMENTO[execution],
        };
      }
      if (status === 'CONCLUSA') return { stateKind: 'muted', stateLabel: 'Conclusa' };
      if (status === 'CONFERMATA') return { stateKind: 'ok', stateLabel: 'Confermata' };
      return { stateKind: 'attn', stateLabel: 'Programmato' };
    };

    // ---- richieste dall'app cliente: UNA riga per richiesta --------------
    // Non hanno tratte: l'andata e l'eventuale ritorno stanno sulla stessa
    // riga (per questo la seconda ora è etichettata "Rit", non "Arr").
    //
    // ⚠️ Il "transfer" delle richieste è l'escort dei booking: stesso servizio,
    // nome diverso (vedi ServiceChip). Il valore originale resta su rawService
    // perché solo il transfer ha mezzo e passeggeri.
    // Partenza e arrivo di una RICHIESTA.
    //
    // Solo il transfer ha due punti salvati. Nel mondo pickup (pickup,
    // auto di cortesia, notturno) la richiesta memorizza UN punto — quello
    // del cliente — e l'altro capo è la SEDE del partner, che non è un
    // `point`: leggendo solo `dropoff_point` l'arrivo restava vuoto.
    //
    // La direzione è la stessa che applica la conversione
    // (booking-request.service.ts): 'gone' = punto→sede, 'return' = sede→punto.
    // Il notturno senza punto di partenza è il caso "dalla sede", quindi
    // sede→punto.
    const requestEnds = (r) => {
      const hq = r.headquarter;
      const sede = hq
        ? (hq.name || place(hq) || 'Sede partner')
        : 'Sede da assegnare';

      if (r.service === 'transfer' || r.service === 'escort') {
        return { from: place(r.pickup_point), to: place(r.dropoff_point) };
      }

      // il punto del cliente: sul notturno "dalla sede" è il dropoff.
      // Se manca del tutto la richiesta è malformata (la validazione lo
      // impone in creazione): meglio dirlo che lasciare la cella vuota.
      const cliente = place(r.pickup_point)
        || place(r.dropoff_point)
        || 'Indirizzo non indicato';
      const dallaSede = r.leg_type === 'return_only'
        || (r.service === 'restaurant' && !r.pickup_point);

      return dallaSede
        ? { from: sede, to: cliente }
        : { from: cliente, to: sede };
    };

    const requestToRow = (r) => {
      const st = requestState(r.status);
      const submittedAt = r.submitted_at || null;
      const waitingDays = submittedAt
        ? Math.floor((Date.now() - new Date(submittedAt).getTime()) / DAY_MS)
        : null;
      const legLabel = {
        round_trip: 'A/R',
        outbound_only: '↗ Solo andata',
        return_only: '↘ Solo ritorno',
      }[r.leg_type];

      return {
        id: r.id,
        _rowKey: `req-${r.id}`,
        kind: 'request',
        shortId: (r.id || '').slice(0, 8),
        service: r.service === 'transfer' ? 'escort' : r.service,
        direction: legLabel || (r.return_requested ? 'A/R' : ''),
        customer:
          [r.customer?.name, r.customer?.surname].filter(Boolean).join(' ')
          || 'Cliente app',
        phone: r.customer?.telephone_number || r.contact || '',
        partnerId: r.partnerId || '',
        provinceId: r.headquarter?.provinceId || '',
        partner: '',
        headquarter: '',
        date: r.outbound_date || r.requested_date,
        start: joinDateTime(r.outbound_date, r.outbound_time),
        end: joinDateTime(r.return_date, r.return_time),
        endLabel: 'Rit',
        ...requestEnds(r),
        driver: '',
        jolly: '',
        needsJolly: false,
        stateKind: st.kind,
        stateLabel: st.label,
        status: r.status,
        submittedAt,
        waitingDays,
        // già convertita: nello stato resta "approvata", ma non è più da
        // lavorare — va detto, altrimenti è indistinguibile.
        bookingId: r.bookingId || '',
      };
    };

    // Una tratta è "da assegnare" se manca il driver — o il jolly, quando il
    // movimento richiede due operatori. Le richieste non hanno tratte: non
    // entrano mai in questo conteggio.
    const isUnassigned = (r) =>
      r.kind !== 'request' && (!r.driver || (r.needsJolly && !r.jolly));

    // Filtri applicati sulle righe già unite: il servizio dipende dalla
    // tratta, e "da assegnare" il server non può calcolarlo (vedi bookingAPI).
    // Isolati dal segmento ATTIVO così
    // che i conteggi delle altre card possano riusare esattamente la stessa
    // regola: un numero sulla card che non corrisponde alle righe che si
    // vedono cliccandola è peggio di nessun numero.
    const applyFilters = (list, seg) => {
      let out = list;
      if (seg === 'assegnare') out = out.filter(isUnassigned);
      // «Futuri» sono le corse già pronte: finché manca qualcuno restano solo
      // in «Da assegnare».
      if (seg === 'futuri') out = out.filter((r) => !isUnassigned(r));
      // Su /booking-request stato, partner e ricerca il server non li filtra
      // (l'endpoint non è crud: accetta solo ?status=, e il partner è un id
      // senza nome). Il volume è piccolo: si filtra qui.
      if (seg === 'richieste') {
        if (requestStatus.value) {
          out = out.filter((r) => r.status === requestStatus.value);
        }
        if (partnerId.value) out = out.filter((r) => r.partnerId === partnerId.value);
        if (provinceId.value) {
          // la sede della richiesta, se c'è; altrimenti un partner della provincia
          const ids = currentProvince.value?.partnerIds || [];
          out = out.filter((r) => (r.provinceId
            ? r.provinceId === provinceId.value
            : ids.includes(r.partnerId)));
        }
        if (search.value) {
          const q = search.value.trim().toLowerCase();
          out = out.filter((r) => r.customer.toLowerCase().includes(q));
        }
      }
      if (serviceFilter.value) {
        out = out.filter((r) =>
          serviceFilter.value === 'journey'
            ? r.service === 'journey'
            // «Notturno» comprende anche i Transfer dei partner notturni.
            : r.service?.startsWith(serviceFilter.value)
              || (serviceFilter.value === 'restaurant' && r.notturno),
        );
      }
      return out;
    };

    const filteredRows = computed(() => applyFilters(rows.value, segment.value));

    // La finestra è già limitata: la paginazione qui è solo di comodità
    // visiva sulle righe in memoria, non genera altre chiamate.
    const visibleRows = computed(() => {
      const from = (page.value - 1) * PAGE_SIZE;
      return filteredRows.value.slice(from, from + PAGE_SIZE);
    });

    const total = computed(() => filteredRows.value.length);
    const pageCount = computed(() =>
      Math.max(1, Math.ceil(filteredRows.value.length / PAGE_SIZE)),
    );

    // Le richieste hanno una sorgente sola e nessuna finestra temporale: si
    // scaricano tutte (sono poche) e si filtrano a valle, così il numero sulla
    // card resta quello delle "da valutare" anche cambiando sotto-filtro.
    const loadRequests = async () => {
      try {
        const list = await bookingRequestAPI.getRequests();
        rows.value = list.map(requestToRow);
        counts.value.richieste = list.filter((r) => r.status === 'pending').length;
      } catch (e) {
        console.error('Richieste non caricate:', e);
        rows.value = [];
        counts.value.richieste = null;
      }
      capped.value = false;
    };

    // Recupero di UN segmento: due sorgenti (booking + journey), appiattite a
    // tratte e ordinate per orario di partenza. Estratta da `load` perché la
    // usano anche i conteggi delle card: contare con una query diversa da
    // quella che popola la lista è il modo sicuro per farle divergere.
    // Ritorna anche `capped`, perché su una finestra troncata il numero è un
    // minimo e non un totale.
    const fetchSegment = async (seg, periodo = null) => {
      const { from, to } = segmentWindow(seg, periodo);
      // Il journey usa un limite superiore ESCLUSIVO: +1 giorno per
      // includere anche l'ultima giornata della finestra.
      const toExclusive = new Date(to);
      toExclusive.setDate(toExclusive.getDate() + 1);

      // Lo spostamento interno non ha una sede: con una provincia scelta si
      // riconosce dai partner che lì ne hanno una. Nessun partner vuol dire
      // nessuno spostamento, non tutti.
      let provincePartners = null;
      if (provinceId.value) {
        const list = provinces.value.length ? provinces.value : await loadProvinces();
        provincePartners = list.find((p) => p.id === provinceId.value)?.partnerIds || [];
      }

      const [bk, jr] = await Promise.allSettled([
        bookingAPI.getBookingsInWindow({
          segment: seg,
          partnerId: partnerId.value,
          provinceId: provinceId.value,
          search: search.value,
          periodo,
        }),
        // Gli spostamenti interni restano fuori in due casi:
        //  - "in conferma": non hanno il concetto di "accettato dal cliente";
        //  - ricerca attiva: la casella cerca per COGNOME CLIENTE e i journey
        //    non hanno cliente, quindi il server non li filtrava e restavano
        //    in lista come falsi risultati (era la card "Futuri" che non
        //    scendeva a zero cercando un nome inesistente).
        seg === 'conferma' || search.value.trim()
          || (provincePartners && !provincePartners.length)
          ? Promise.resolve(null)
          : journeyAPI.getJourneysByRange(
            from,
            toExclusive.toISOString().slice(0, 10),
            partnerId.value || undefined,
            provincePartners,
          ),
      ]);

      const bookingRows =
        bk.status === 'fulfilled' ? (bk.value.rows || []).flatMap(toRows) : [];
      let journeyList =
        jr.status === 'fulfilled' && jr.value
          ? jr.value.data?.data ?? jr.value.data ?? []
          : [];
      // Gli spostamenti si smistano per CORSA, non per giornata: una corsa
      // conclusa (o col veicolo già consegnato) passa subito in «Conclusi»,
      // anche se le altre corse della giornata sono ancora da fare. Prima ci
      // passava solo quando era conclusa tutta la giornata, e la corsa finita
      // restava fra gli «Attivi».
      if (seg === 'dachiudere') {
        journeyList = journeyList.filter((j) => j.status !== 'ELIMINATA');
      }
      let journeyRows = journeyList.flatMap(journeyToRows);
      // «Conclusi»: solo le corse concluse. Negli altri segmenti (attivi, da
      // assegnare, futuri, da chiudere) solo quelle ancora da fare.
      journeyRows = seg === 'passati'
        ? journeyRows.filter((r) => r.conclusa)
        : journeyRows.filter((r) => !r.conclusa);

      if (bk.status === 'rejected') console.error('Booking non caricati:', bk.reason);
      if (jr.status === 'rejected') console.error('Journey non caricati:', jr.reason);

      // Se ENTRAMBE le sorgenti falliscono non ci sono zero tratte: non si sa
      // quante siano. Chi conta deve poterlo distinguere.
      const failed = bk.status === 'rejected' && jr.status === 'rejected';

      // Ordine cronologico per ORARIO DI PARTENZA, non per data del booking:
      // è ciò che serve a chi smista la giornata.
      const merged = [...bookingRows, ...journeyRows].sort((a, b) => {
        const ta = a.start ? new Date(a.start).getTime() : Number.MAX_SAFE_INTEGER;
        const tb = b.start ? new Date(b.start).getTime() : Number.MAX_SAFE_INTEGER;
        return seg === 'passati' ? tb - ta : ta - tb;
      });

      return {
        rows: merged,
        failed,
        capped: bk.status === 'fulfilled' && (bk.value.rows || []).length >= WINDOW_CAP,
      };
    };

    const load = async () => {
      loading.value = true;
      if (isRequests.value) {
        await loadRequests();
        loading.value = false;
        return;
      }
      try {
        if (segment.value === 'passati') mesiCaricati.value = [meseCorrente()];
        const res = await fetchSegment(
          segment.value,
          segment.value === 'passati' ? mesiCaricati.value[0] : null,
        );
        rows.value = res.rows;
        capped.value = res.capped;
        counts.value[segment.value] = res.failed ? null : filteredRows.value.length;
      } catch (e) {
        console.error('Caricamento lista fallito:', e);
        rows.value = [];
      } finally {
        loading.value = false;
      }
    };

    const loadPartners = async () => {
      try {
        const r = await partnerAPI.getPartners();
        partners.value = (r.data?.data ?? r.data ?? []).map((p) => ({
          id: p.id, company_name: p.company_name,
        }));
      } catch (e) {
        console.warn('Elenco partner non disponibile:', e?.message);
      }
    };

    // Stessa chiamata del menu, già in memoria dopo il login.
    const loadProvinces = async () => {
      try {
        provinces.value = await areasAPI.getPartnerProvinces();
      } catch (e) {
        console.warn('Elenco province non disponibile:', e?.message);
      }
      return provinces.value;
    };

    // ------------------------------------------------------------------
    //  Conteggi delle card
    //
    //  Il numero di un segmento si sa solo scaricandolo: nessun endpoint lo
    //  conta a database. `/booking/schedule` conta le tratte in SQL ma filtra
    //  la sola finestra temporale — non sa distinguere "in conferma",
    //  "conclusi" e "da assegnare", che qui si ricavano a valle (lo stato sta
    //  sul booking, il driver sulla tratta, il jolly su `planner`).
    //
    //  Quindi si scaricano, ma DOPO aver mostrato il segmento attivo e uno
    //  alla volta: la pagina resta usabile e i numeri compaiono man mano,
    //  invece di far aspettare sei finestre in parallelo all'apertura.
    //
    //  I conteggi dipendono dai filtri attivi (ricerca, partner, servizio),
    //  perché è così che sono contate anche le righe visibili: cambiando un
    //  filtro vanno rifatti, e il giro precedente non deve poter scrivere
    //  sopra a quello nuovo. Da qui il contatore di generazione.
    // ------------------------------------------------------------------
    const COUNTED_SEGMENTS = ['assegnare', 'conferma', 'attivi', 'futuri', 'passati', 'dachiudere'];
    let countGeneration = 0;

    // Aggiunge in coda il mese prima del più vecchio già caricato: le righe
    // restano ordinate dalla più recente e le due sorgenti restano unite.
    const caricaMesePrecedente = async () => {
      if (caricandoMese.value) return;
      const precedente = mesePrima(mesiCaricati.value[mesiCaricati.value.length - 1]);
      caricandoMese.value = true;
      try {
        const res = await fetchSegment('passati', precedente);
        rows.value = [...rows.value, ...res.rows];
        mesiCaricati.value = [...mesiCaricati.value, precedente];
        capped.value = capped.value || res.capped;
        counts.value.passati = filteredRows.value.length;
      } catch (e) {
        console.error('Mese precedente non caricato:', e);
      } finally {
        caricandoMese.value = false;
      }
    };

    // Quante sono in TUTTO: due conteggi indipendenti, ognuno con la sua
    // sorte. Gli spostamenti non hanno un cliente, quindi con una ricerca per
    // nome attiva il loro totale non ha senso e resta «—», come la lista che
    // già li esclude.
    const loadTotali = async () => {
      const cercando = !!search.value.trim();
      const partnerIds = provinceId.value ? (currentProvince.value?.partnerIds || []) : null;
      const [bk, jr] = await Promise.allSettled([
        bookingAPI.countConcluse({
          partnerId: partnerId.value,
          provinceId: provinceId.value,
          search: search.value,
        }),
        cercando
          ? Promise.reject(new Error('ricerca per cliente: gli spostamenti restano fuori'))
          : journeyAPI.countConclusi({ partnerId: partnerId.value, partnerIds }),
      ]);
      totConcluse.value = bk.status === 'fulfilled' ? bk.value : null;
      totConclusi.value = jr.status === 'fulfilled' ? jr.value : null;
    };

    const refreshCounts = async () => {
      const gen = ++countGeneration;
      for (const seg of COUNTED_SEGMENTS) {
        // il segmento aperto l'ha già contato `load`, con gli stessi filtri
        if (seg === segment.value) continue;
        try {
          const res = await fetchSegment(seg, seg === 'passati' ? meseCorrente() : null);
          if (gen !== countGeneration) return; // filtri cambiati: giro scaduto
          counts.value[seg] = res.failed ? null : applyFilters(res.rows, seg).length;
        } catch (e) {
          if (gen !== countGeneration) return;
          // Un conteggio mancante resta "—": meglio di un numero inventato.
          counts.value[seg] = null;
          console.warn(`Conteggio "${seg}" non disponibile:`, e?.message);
        }
      }
    };

    const reload = () => {
      page.value = 1;
      load().then(refreshCounts);
      // i totali dipendono dagli stessi filtri
      loadTotali();
    };

    // Cambiare provincia cambia l'indirizzo; la lista la ricarica il watch,
    // che vale anche per i clic sul menu (stessa pagina, query diversa).
    const setProvince = (id) => {
      const query = { ...route.query };
      if (id) query.provincia = id;
      else delete query.provincia;
      router.replace({ path: route.path, query });
    };
    watch(() => route.query.provincia, () => reload());

    // Cambiare card NON invalida i conteggi: i filtri sono gli stessi, quindi
    // si ricarica la sola lista invece di rifare tutto il giro.
    const setSegment = (key) => {
      if (segment.value === key) return;
      segment.value = key;
      page.value = 1;
      load();
    };

    // La finestra è già in memoria: cambiare pagina non richiede il server.
    const onPageChange = (p) => { page.value = p; };

    let timer = null;
    const onSearchInput = () => {
      clearTimeout(timer);
      timer = setTimeout(reload, 350);
    };

    const resetFilters = () => {
      search.value = '';
      partnerId.value = '';
      serviceFilter.value = '';
      requestStatus.value = '';
      // con una provincia nell'indirizzo la ricarica parte dal watch
      if (provinceId.value) setProvince('');
      else reload();
    };

    // Tre mondi, tre pagine: richieste e spostamenti interni non sono
    // booking e i loro id non aprono /booking/detail.
    const openDetail = (row) => {
      if (!row?.id) return;
      const path = row.kind === 'request'
        ? `/booking/request/${row.id}`
        : row.kind === 'journey'
          ? `/journey/detail/${row.id}`
          : `/booking/detail/${row.id}`;
      router.push(path);
    };

    // ---- formattazione ---------------------------------------------------
    const hhmm = (v) => {
      if (!v) return '—';
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return '—';
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };

    const dayLabel = (v) => {
      if (!v) return '';
      const d = new Date(v);
      const today = new Date();
      const same = (a, b) => a.toDateString() === b.toDateString();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      if (same(d, today)) return 'Oggi';
      if (same(d, tomorrow)) return 'Domani';
      return d.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
    };

    const waitLabel = (row) => {
      if (row.waitingDays == null) return '';
      if (row.waitingDays === 0) return 'oggi';
      if (row.waitingDays === 1) return 'da 1 giorno';
      return `da ${row.waitingDays} giorni`;
    };

    const emptyText = computed(() =>
      isRequests.value
        ? 'Nessuna richiesta corrisponde ai filtri'
        : 'Nessun booking corrisponde ai filtri');

    // Bordino colorato per servizio, e basta: il fondo resta bianco.
    // Che una tratta sia da assegnare lo dicono già la pill di stato e il
    // "Driver non assegnato" in arancione, senza velare l'intera riga.
    // Le richieste hanno un bordino proprio: non sono prenotazioni.
    const rowClass = ({ row }) => {
      if (row.kind === 'request') return 'bkl-row bkl-row--req';
      const svc = row.service === 'journey'
        ? 'journey'
        : row.service?.startsWith('escort')
          ? 'transfer'
          : row.service === 'restaurant'
            ? 'notturno'
            : 'pickup';
      return `bkl-row bkl-row--${svc}`;
    };

    // Il numero sulla card "Richieste" è un avviso: deve esserci anche
    // atterrando su un altro segmento.
    const loadPendingCount = async () => {
      try {
        const list = await bookingRequestAPI.getRequests('pending');
        counts.value.richieste = list.length;
      } catch (e) {
        console.warn('Conteggio richieste non disponibile:', e?.message);
      }
    };

    onMounted(() => {
      // I conteggi partono solo a lista mostrata: sono cinque finestre in più
      // e non devono ritardare quello che si sta guardando.
      load().then(refreshCounts);
      loadTotali();
      loadPartners();
      loadProvinces();
      if (!isRequests.value) loadPendingCount();
    });

    return {
      segment, rows, visibleRows, loading, page, total, pageCount, capped,
      search, partnerId, serviceFilter, partners, kpisTodo, kpisCiclo, pageSize: PAGE_SIZE,
      provinces, provinceId, currentProvince, setProvince,
      isRequests, requestStatus, emptyText,
      caricaMesePrecedente, caricandoMese, storicoLabel, mesePrecedenteLabel,
      setSegment, onPageChange, onSearchInput, resetFilters, reload, openDetail,
      partnerOf, waitLabel,
      hhmm, dayLabel, rowClass, windowCap: WINDOW_CAP, ini: initials,
    };
  },
};
</script>

<style scoped>
.bkl { padding: 4px 0 32px; }

/* ---- i due pulsanti di creazione ------------------------------------------
   Colori presi dai token di servizio, gli stessi delle pastiglie nelle righe:
   viola = spostamento interno, giallo = pickup. `:deep()` perché el-button è
   un componente figlio e lo style qui è scoped.

   Sul giallo il testo NON può essere bianco (contrasto insufficiente): si usa
   l'inchiostro scuro già definito per quel colore, `--svc-pickup-ink`.
   Element Plus imposta i propri colori con specificità alta, quindi vanno
   ridichiarati anche in :hover, altrimenti al passaggio del mouse tornano
   quelli di default. */
.bkl__cta {
  font-weight: 600;
}
:deep(.bkl__cta--fleet),
:deep(.bkl__cta--fleet:hover),
:deep(.bkl__cta--fleet:focus) {
  background: var(--svc-fleet);
  border-color: var(--svc-fleet);
  color: #fff;
}
:deep(.bkl__cta--fleet:hover) {
  filter: brightness(1.08);
}
:deep(.bkl__cta--booking),
:deep(.bkl__cta--booking:hover),
:deep(.bkl__cta--booking:focus) {
  background: var(--svc-pickup);
  border-color: var(--svc-pickup);
  color: var(--svc-pickup-ink);
}
:deep(.bkl__cta--booking:hover) {
  filter: brightness(1.05);
}

.bkl__head {
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 16px; flex-wrap: wrap; margin-bottom: 18px;
}
.bkl__title { margin: 0; font-size: 22px; color: var(--text-primary); }
.bkl__sub { margin: 4px 0 0; font-size: 13px; color: var(--text-tertiary); }
.bkl__headActions { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.bkl__search { width: 250px; }

.bkl__note {
  margin: 0 0 10px; padding: 8px 12px; border-radius: 8px;
  border: 1px solid var(--border-color); background: var(--bg-secondary);
  font-size: 12.5px; color: var(--text-secondary);
}

/* card di riepilogo = filtro di stato */
.bkl__kpis { display: grid; gap: 12px; margin-bottom: 12px; }
.bkl__kpis--todo { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.bkl__kpis--ciclo { grid-template-columns: repeat(4, minmax(0, 1fr)); margin-bottom: 16px; }
@media (max-width: 1200px) {
  .bkl__kpis--todo, .bkl__kpis--ciclo { grid-template-columns: repeat(3, 1fr); }
}
@media (max-width: 900px) {
  .bkl__kpis--todo, .bkl__kpis--ciclo { grid-template-columns: repeat(2, 1fr); }
}
.bkl__kpi {
  text-align: left; background: var(--bg-primary);
  border: 1px solid var(--border-color); border-radius: var(--radius-lg);
  padding: 13px 15px; cursor: pointer; font-family: inherit;
  transition: border-color var(--transition-fast);
}
.bkl__kpi:hover { border-color: var(--brand-violet); }
.bkl__kpi.is-active {
  border-color: var(--brand-violet);
  box-shadow: inset 0 0 0 1px var(--brand-violet);
}
.bkl__kpiNum {
  display: block; font-size: 23px; font-weight: 750; letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums; color: var(--text-primary);
}
.bkl__kpi.is-attn .bkl__kpiNum { color: var(--state-attn); }
/* Le richieste non sono prenotazioni: la card è staccata dalle altre. */
.bkl__kpi.is-req { border-style: dashed; }
.bkl__kpi.is-req.is-active { border-style: solid; }
.bkl__kpiHint { display: block; font-size: 11px; color: var(--text-tertiary); margin-top: 2px; }
.bkl__storico {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 14px;
  border-top: 1px solid var(--border-subtle, #eceef5);
  font-size: 12.5px;
  color: var(--text-tertiary);
}
.bkl__kpiLbl {
  display: block; margin-top: 2px; font-size: 11.5px; font-weight: 600;
  letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-tertiary);
}

.bkl__card {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); overflow: hidden;
}
.bkl__toolbar {
  display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap;
  padding: 12px 14px; border-bottom: 1px solid var(--border-color);
}
.bkl__field label {
  display: block; margin-bottom: 3px; font-size: 11px; font-weight: 600;
  letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-tertiary);
}
.bkl__spacer { flex: 1; }

/* orari impilati, allineati al percorso */
.bkl__when { display: flex; flex-direction: column; gap: 2px; }
.bkl__times { display: flex; align-items: baseline; gap: 8px; }
.bkl__tl {
  /* «Part» non deve andare a capo: larghezza minima, mai spezzata. */
  flex: none; min-width: 28px; white-space: nowrap; font-size: 9.5px; font-weight: 700; letter-spacing: 0.03em;
  text-transform: uppercase; color: var(--text-tertiary);
}
.bkl__tt { font-weight: 700; font-size: 14px; font-variant-numeric: tabular-nums; }
.bkl__times--arr .bkl__tt { font-weight: 600; color: var(--text-secondary); }
.bkl__chips { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-top: 5px; }
.bkl__day {
  font-size: 11px; font-weight: 650; color: var(--text-secondary);
  background: var(--bg-tertiary); border: 1px solid var(--border-color);
  border-radius: 20px; padding: 1px 8px;
}
/* marchio "Richiesta": deve leggersi prima del servizio */
.bkl__req {
  font-size: 10.5px; font-weight: 750; letter-spacing: 0.05em;
  text-transform: uppercase; padding: 1px 7px; border-radius: 5px;
  color: var(--brand-violet); background: var(--bg-primary);
  border: 1px dashed var(--brand-violet);
}
/* tipo di spostamento interno: stessi colori del servizio */
.bkl__tipo {
  font-size: 10.5px; font-weight: 700; padding: 1px 7px; border-radius: 5px;
  color: var(--svc-fleet); background: var(--svc-fleet-bg);
}
.bkl__plate { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
/* targa prova: condizione del veicolo, colore d'attenzione */
.bkl__prova {
  font-size: 10.5px; font-weight: 700; padding: 1px 7px; border-radius: 5px;
  color: #92400e; background: #fef3c7;
}
/* direzione: neutra di proposito, non è un servizio */
.bkl__leg {
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.04em;
  text-transform: uppercase; padding: 1px 6px; border-radius: 5px;
  color: var(--state-muted); background: var(--state-muted-bg);
  border: 1px solid var(--state-muted-line);
}

/* percorso: stessa icona, due colori */
.bkl__route { display: flex; flex-direction: column; gap: 6px; }
.bkl__pt { display: flex; align-items: flex-start; gap: 8px; }
.bkl__pin { width: 9px; height: 9px; border-radius: 50%; margin-top: 5px; flex: none; }
.bkl__pin--from { background: var(--text-tertiary); }
.bkl__pin--to { background: var(--brand-violet); }
.bkl__addr { font-size: 13px; line-height: 1.3; }

.bkl__cust { font-weight: 600; }
.bkl__meta { font-size: 12px; color: var(--text-tertiary); }
.bkl__id {
  margin-top: 2px; font-size: 11.5px; font-variant-numeric: tabular-nums;
  color: var(--brand-violet);
}
.bkl__driver { display: flex; align-items: center; gap: 8px; }
.bkl__avatar {
  width: 26px; height: 26px; flex: none; border-radius: 50%;
  display: grid; place-items: center; background: var(--avatar-bg);
  color: #fff; font-size: 10px; font-weight: 700;
}
.bkl__unassigned { color: var(--state-attn); font-weight: 650; font-size: 12.5px; display: block; }
.bkl__driver--jolly { margin-top: 4px; }
.bkl__avatar--j { background: var(--svc-fleet); }
.bkl__role {
  font-size: 10px; font-weight: 700; letter-spacing: 0.03em;
  text-transform: uppercase; color: var(--svc-fleet);
}
.bkl__capped { display: block; margin-top: 2px; color: var(--state-attn); }

.bkl__foot {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap; padding: 12px 14px;
  border-top: 1px solid var(--border-color);
}
.bkl__footInfo { font-size: 12.5px; color: var(--text-tertiary); }

/* bordino sinistro = servizio; velatura = tratta senza driver */
.bkl :deep(.bkl-row) { cursor: pointer; }
.bkl :deep(.bkl-row--pickup) td:first-child { box-shadow: inset 6px 0 0 var(--svc-pickup); }
.bkl :deep(.bkl-row--transfer) td:first-child { box-shadow: inset 6px 0 0 var(--svc-transfer); }
.bkl :deep(.bkl-row--notturno) td:first-child { box-shadow: inset 6px 0 0 var(--svc-notturno); }
.bkl :deep(.bkl-row--journey) td:first-child { box-shadow: inset 6px 0 0 var(--svc-fleet); }
.bkl :deep(.bkl-row--req) td:first-child { box-shadow: inset 6px 0 0 var(--brand-violet); }

.bkl__meta--stale { color: var(--state-attn); font-weight: 650; }

/* header chiaro: l'override globale impone un gradiente viola con !important */
.bkl :deep(.el-table th.el-table__cell) {
  background: var(--bg-tertiary) !important;
  color: var(--text-tertiary) !important;
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
  padding: 11px 12px; border-bottom: 1px solid var(--border-color);
}
</style>
